import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/ReportDesigner.css';
import { REPORT_CATEGORIES, parseDisplayConfig, parseFilterDefs } from '../../utils/reportDisplay';
import { REPORT_ICON_OPTIONS } from '../../utils/reportIcons';
import { reportQueryErrorMessage } from '../../utils/reportQueryError';

const emptyDisplay = () => ({
  columns: {},
  grouping: { columns: [], showSubtotals: true, collapsedByDefault: false },
  aggregates: { grandTotal: false, columns: {} },
  defaultSort: [],
});

const emptyDraft = () => ({
  report_title: '',
  category: 'Uncategorized',
  description: '',
  sort_order: 0,
  icon: 'faFileAlt',
  permission_roles: 'Admin',
  query_template: 'SELECT\n  p.patient_id,\n  p.patient_number,\n  p.patient_first_name,\n  p.patient_last_name\nFROM patients p\nWHERE (:startDate IS NULL OR p.registration_date >= :startDate)\n  AND (:endDate IS NULL OR p.registration_date <= :endDate)\nORDER BY p.patient_id DESC',
  filters: [
    { name: 'startDate', label: 'Start Date', type: 'date', default: '' },
    { name: 'endDate', label: 'End Date', type: 'date', default: '' },
  ],
  display: emptyDisplay(),
});

const ReportDesigner = () => {
  const { reportId } = useParams();
  const isNew = reportId === 'new';
  const navigate = useNavigate();
  const [draft, setDraft] = useState(emptyDraft());
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState('');
  const [advancedDisplay, setAdvancedDisplay] = useState('');
  const [instruction, setInstruction] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiLog, setAiLog] = useState([]);
  const [pendingAi, setPendingAi] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [previewError, setPreviewError] = useState('');

  const selectedRoles = useMemo(
    () => String(draft.permission_roles || '').split(',').map((role) => role.trim()).filter(Boolean),
    [draft.permission_roles]
  );

  const previewColumns = previewRows[0] ? Object.keys(previewRows[0]) : [];
  const displayColumns = useMemo(() => {
    const fromConfig = Object.keys((draft.display && draft.display.columns) || {});
    return Array.from(new Set([...previewColumns, ...fromConfig]));
  }, [previewColumns, draft.display]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/roles`)
      .then((res) => setRoles(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRoles([]));
  }, []);

  useEffect(() => {
    if (isNew) {
      const initial = emptyDraft();
      setDraft(initial);
      setFilterValues(defaultsFromFilters(initial.filters));
      setLoading(false);
      return;
    }
    setLoading(true);
    axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/reports/${reportId}`)
      .then((res) => {
        const report = res.data;
        const next = {
          report_title: report.report_title || '',
          category: report.category || 'Uncategorized',
          description: report.description || '',
          sort_order: report.sort_order || 0,
          icon: report.icon || 'faFileAlt',
          permission_roles: report.permission_roles || 'Admin',
          query_template: report.query_template || '',
          filters: parseFilterDefs(report.filters),
          display: parseDisplayConfig(report.display),
        };
        if (!next.display.grouping) next.display.grouping = { columns: [], showSubtotals: true, collapsedByDefault: false };
        if (!next.display.aggregates) next.display.aggregates = { grandTotal: false, columns: {} };
        if (!next.display.columns) next.display.columns = {};
        setDraft(next);
        setFilterValues(defaultsFromFilters(next.filters));
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || 'Could not load report.');
      })
      .finally(() => setLoading(false));
  }, [reportId, isNew]);

  const updateDraft = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const updateDisplay = (patch) => setDraft((prev) => ({ ...prev, display: { ...prev.display, ...patch } }));

  const toggleRole = (roleName) => {
    const next = selectedRoles.includes(roleName)
      ? selectedRoles.filter((role) => role !== roleName)
      : [...selectedRoles, roleName];
    updateDraft({ permission_roles: next.join(',') });
  };

  const updateFilter = (index, patch) => {
    const filters = draft.filters.map((filter, idx) => (idx === index ? { ...filter, ...patch } : filter));
    updateDraft({ filters });
  };

  const addFilter = () => {
    updateDraft({
      filters: [...draft.filters, { name: `filter_${draft.filters.length + 1}`, label: 'New filter', type: 'text', default: '' }],
    });
  };

  const removeFilter = (index) => {
    updateDraft({ filters: draft.filters.filter((_, idx) => idx !== index) });
  };

  const updateColumn = (columnName, patch) => {
    const columns = { ...(draft.display.columns || {}) };
    columns[columnName] = { ...(columns[columnName] || {}), ...patch };
    updateDisplay({ columns });
  };

  const payloadFromDraft = () => ({
    report_title: draft.report_title,
    category: draft.category || 'Uncategorized',
    description: draft.description || null,
    sort_order: Number(draft.sort_order) || 0,
    icon: draft.icon || null,
    permission_roles: draft.permission_roles,
    query_template: draft.query_template,
    filters: draft.filters,
    display: draft.display,
  });

  const runPreview = async () => {
    setPreviewing(true);
    setPreviewError('');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/reports/preview`,
        {
          query_template: draft.query_template,
          filters: draft.filters,
          filterValues,
          preview: true,
        }
      );
      const rows = Array.isArray(res.data) ? res.data : [];
      setPreviewRows(rows);
      if (rows.length && Object.keys(draft.display.columns || {}).length === 0) {
        const columns = {};
        Object.keys(rows[0]).forEach((name) => {
          columns[name] = {};
        });
        updateDisplay({ columns });
      }
      toast.success(`Preview returned ${rows.length} row(s).`);
    } catch (err) {
      const message = reportQueryErrorMessage(err, 'Preview failed.');
      setPreviewRows([]);
      setPreviewError(message);
      toast.error(message, { autoClose: 12000 });
    } finally {
      setPreviewing(false);
    }
  };

  const saveReport = async () => {
    if (!draft.report_title.trim()) {
      toast.error('Title is required.');
      return;
    }
    if (!draft.permission_roles.trim()) {
      toast.error('Select at least one role.');
      return;
    }
    setSaving(true);
    try {
      const body = payloadFromDraft();
      if (isNew) {
        const res = await axios.post(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/reports`, body);
        toast.success('Report created.');
        navigate(`/reports/${res.data.report_id}/edit`, { replace: true });
      } else {
        await axios.put(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/reports/${reportId}`, body);
        toast.success('Report saved.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const applyAdvancedJson = () => {
    try {
      const filters = JSON.parse(advancedFilters || '[]');
      const display = JSON.parse(advancedDisplay || '{}');
      if (!Array.isArray(filters)) throw new Error('Filters must be a JSON array.');
      updateDraft({ filters, display: { ...emptyDisplay(), ...parseDisplayConfig(display) } });
      toast.success('Advanced JSON applied.');
    } catch (err) {
      toast.error(err.message || 'Invalid JSON.');
    }
  };

  const askAi = async () => {
    if (!instruction.trim()) {
      toast.error('Describe the report change you want.');
      return;
    }
    setAiBusy(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/ai/report-builder`,
        {
          instruction: instruction.trim(),
          currentDraft: {
            report_title: draft.report_title,
            query_template: draft.query_template,
            filters: draft.filters,
            display: draft.display,
          },
        }
      );
      const data = res.data?.data || {};
      const sqlValid = res.data?.sql_valid !== false;
      setPendingAi({ ...data, sqlValid, sqlError: res.data?.sql_error, warnings: data.warnings || res.data?.warnings });
      setAiLog((prev) => [
        ...prev,
        { role: 'user', text: instruction.trim() },
        { role: 'assistant', text: data.explanation || 'Draft ready.', warnings: data.warnings, sqlValid },
      ]);
      setInstruction('');
      if (res.data?.sql_valid === false) {
        toast.warn(res.data?.sql_error || 'AI SQL did not pass validation. Review before applying.');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'AI request failed.');
    } finally {
      setAiBusy(false);
    }
  };

  const applyAiDraft = () => {
    if (!pendingAi) return;
    const next = { ...draft };
    if (pendingAi.report_title) next.report_title = pendingAi.report_title;
    if (pendingAi.query_template) next.query_template = pendingAi.query_template;
    if (pendingAi.filters) next.filters = parseFilterDefs(pendingAi.filters);
    if (pendingAi.display) next.display = { ...emptyDisplay(), ...parseDisplayConfig(pendingAi.display) };
    setDraft(next);
    setPendingAi(null);
    toast.success('AI draft applied. Preview, then save.');
  };

  if (loading) {
    return <p style={{ padding: 20 }}>Loading designer…</p>;
  }

  return (
    <div className="report-designer">
      <ToastContainer />
      <div className="report-designer-header">
        <h1>{isNew ? 'New report' : `Edit: ${draft.report_title || reportId}`}</h1>
        <div className="report-designer-header-actions">
          <Link to="/reports" className="report-designer-btn secondary" style={{ textDecoration: 'none' }}>Back to list</Link>
          {!isNew && (
            <Link to={`/reports-dashboard/${reportId}`} className="report-designer-btn secondary" style={{ textDecoration: 'none' }}>Open runner</Link>
          )}
          <button type="button" className="report-designer-btn secondary" onClick={runPreview} disabled={previewing}>
            {previewing ? 'Previewing…' : 'Preview (100 rows)'}
          </button>
          <button type="button" className="report-designer-btn success" onClick={saveReport} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="report-designer-layout">
        <div className="report-designer-main">
          <section className="report-designer-section">
            <h2>Details</h2>
            <div className="report-designer-grid">
              <div className="report-designer-field">
                <label htmlFor="report_title">Title</label>
                <input id="report_title" value={draft.report_title} onChange={(e) => updateDraft({ report_title: e.target.value })} />
              </div>
              <div className="report-designer-field">
                <label htmlFor="category">Category</label>
                <input
                  id="category"
                  list="report-categories"
                  value={draft.category}
                  onChange={(e) => updateDraft({ category: e.target.value })}
                />
                <datalist id="report-categories">
                  {REPORT_CATEGORIES.map((name) => <option key={name} value={name} />)}
                </datalist>
              </div>
              <div className="report-designer-field">
                <label htmlFor="sort_order">Sort order</label>
                <input id="sort_order" type="number" value={draft.sort_order} onChange={(e) => updateDraft({ sort_order: e.target.value })} />
              </div>
              <div className="report-designer-field">
                <label htmlFor="icon">Icon</label>
                <select id="icon" value={draft.icon || 'faFileAlt'} onChange={(e) => updateDraft({ icon: e.target.value })}>
                  {REPORT_ICON_OPTIONS.map((option) => (
                    <option key={option.name} value={option.name}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="report-designer-field" style={{ marginTop: 10 }}>
              <label htmlFor="description">Description</label>
              <input id="description" value={draft.description} onChange={(e) => updateDraft({ description: e.target.value })} />
            </div>
            <div className="report-designer-field" style={{ marginTop: 10 }}>
              <label>Permission roles</label>
              <div className="report-designer-roles">
                {roles.map((role) => (
                  <label key={role.name || role.id}>
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(role.name)}
                      onChange={() => toggleRole(role.name)}
                    />
                    {role.name}
                  </label>
                ))}
              </div>
            </div>
          </section>

          <section className="report-designer-section">
            <h2>SQL</h2>
            <p className="report-designer-muted">Use named placeholders that match filter names, e.g. :startDate. SELECT / WITH only.</p>
            <textarea
              className="report-designer-sql"
              value={draft.query_template}
              onChange={(e) => updateDraft({ query_template: e.target.value })}
            />
            {previewError && (
              <div className="report-designer-error" role="alert">
                <strong>Preview error</strong>
                <pre className="report-sql-error-detail">{previewError}</pre>
              </div>
            )}
          </section>

          <section className="report-designer-section">
            <div className="report-designer-row">
              <h2>Filters</h2>
              <button type="button" className="report-designer-btn secondary" onClick={addFilter}>Add filter</button>
            </div>
            {draft.filters.map((filter, index) => (
              <div key={`${filter.name}-${index}`} className="report-designer-filter-card">
                <div className="report-designer-row">
                  <strong>{filter.label || filter.name}</strong>
                  <button type="button" className="report-designer-btn secondary" onClick={() => removeFilter(index)}>Remove</button>
                </div>
                <div className="report-designer-grid">
                  <div className="report-designer-field">
                    <label>Name</label>
                    <input value={filter.name || ''} onChange={(e) => updateFilter(index, { name: e.target.value })} />
                  </div>
                  <div className="report-designer-field">
                    <label>Label</label>
                    <input value={filter.label || ''} onChange={(e) => updateFilter(index, { label: e.target.value })} />
                  </div>
                  <div className="report-designer-field">
                    <label>Type</label>
                    <select value={filter.type || 'text'} onChange={(e) => updateFilter(index, { type: e.target.value })}>
                      <option value="text">text</option>
                      <option value="date">date</option>
                      <option value="number">number</option>
                      <option value="dropdown">dropdown</option>
                    </select>
                  </div>
                  <div className="report-designer-field">
                    <label>Default</label>
                    <input value={filter.default || ''} onChange={(e) => updateFilter(index, { default: e.target.value })} />
                  </div>
                  <div className="report-designer-field">
                    <label>Preview value</label>
                    <input
                      value={filterValues[filter.name] || ''}
                      onChange={(e) => setFilterValues((prev) => ({ ...prev, [filter.name]: e.target.value }))}
                    />
                  </div>
                </div>
                {filter.type === 'dropdown' && (
                  <div className="report-designer-field" style={{ marginTop: 8 }}>
                    <label>Options query (optional)</label>
                    <textarea
                      value={filter.optionsQuery || ''}
                      onChange={(e) => updateFilter(index, { optionsQuery: e.target.value })}
                      placeholder="SELECT id, name AS label FROM ..."
                    />
                  </div>
                )}
              </div>
            ))}
          </section>

          <section className="report-designer-section">
            <h2>Display</h2>
            <div className="report-designer-grid">
              <div className="report-designer-field">
                <label>Group by (comma-separated column names)</label>
                <input
                  value={(draft.display.grouping?.columns || []).join(', ')}
                  onChange={(e) => updateDisplay({
                    grouping: {
                      ...(draft.display.grouping || {}),
                      columns: e.target.value.split(',').map((item) => item.trim()).filter(Boolean),
                    },
                  })}
                />
              </div>
              <div className="report-designer-field">
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(draft.display.grouping?.showSubtotals)}
                    onChange={(e) => updateDisplay({
                      grouping: { ...(draft.display.grouping || {}), showSubtotals: e.target.checked },
                    })}
                  /> Subtotals
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(draft.display.grouping?.collapsedByDefault)}
                    onChange={(e) => updateDisplay({
                      grouping: { ...(draft.display.grouping || {}), collapsedByDefault: e.target.checked },
                    })}
                  /> Collapse groups by default
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(draft.display.aggregates?.grandTotal)}
                    onChange={(e) => updateDisplay({
                      aggregates: { ...(draft.display.aggregates || {}), grandTotal: e.target.checked },
                    })}
                  /> Grand total
                </label>
              </div>
            </div>

            {displayColumns.map((columnName) => {
              const column = (draft.display.columns || {})[columnName] || {};
              const linkTo = column.linkTo || {};
              return (
                <div key={columnName} className="report-designer-column-card">
                  <div className="report-designer-row">
                    <strong>{columnName}</strong>
                  </div>
                  <div className="report-designer-grid">
                    <div className="report-designer-field">
                      <label>Format</label>
                      <select
                        value={column.format?.type || ''}
                        onChange={(e) => updateColumn(columnName, {
                          format: e.target.value ? { ...(column.format || {}), type: e.target.value } : undefined,
                        })}
                      >
                        <option value="">None</option>
                        <option value="currency">currency</option>
                        <option value="number">number</option>
                        <option value="percentage">percentage</option>
                        <option value="date">date</option>
                        <option value="datetime">datetime</option>
                      </select>
                    </div>
                    <div className="report-designer-field">
                      <label>Aggregate</label>
                      <select
                        value={(draft.display.aggregates?.columns || {})[columnName] || ''}
                        onChange={(e) => {
                          const columns = { ...(draft.display.aggregates?.columns || {}) };
                          if (e.target.value) columns[columnName] = e.target.value;
                          else delete columns[columnName];
                          updateDisplay({ aggregates: { ...(draft.display.aggregates || {}), columns } });
                        }}
                      >
                        <option value="">None</option>
                        <option value="sum">sum</option>
                        <option value="count">count</option>
                        <option value="avg">avg</option>
                        <option value="min">min</option>
                        <option value="max">max</option>
                      </select>
                    </div>
                    <div className="report-designer-field">
                      <label>Link</label>
                      <select
                        value={linkTo.type || (linkTo.reportId ? 'report' : '')}
                        onChange={(e) => updateColumn(columnName, {
                          linkTo: e.target.value ? { ...linkTo, type: e.target.value } : undefined,
                        })}
                      >
                        <option value="">None</option>
                        <option value="patient">patient</option>
                        <option value="admission">admission</option>
                        <option value="report">report</option>
                      </select>
                    </div>
                    {(linkTo.type === 'patient' || linkTo.type === 'admission') && (
                      <div className="report-designer-field">
                        <label>ID field</label>
                        <input
                          value={linkTo.idField || ''}
                          onChange={(e) => updateColumn(columnName, { linkTo: { ...linkTo, idField: e.target.value } })}
                          placeholder={linkTo.type === 'patient' ? 'patient_id' : 'admission_id'}
                        />
                      </div>
                    )}
                    {linkTo.type === 'report' && (
                      <>
                        <div className="report-designer-field">
                          <label>Target report ID</label>
                          <input
                            value={linkTo.reportId || ''}
                            onChange={(e) => updateColumn(columnName, { linkTo: { ...linkTo, type: 'report', reportId: Number(e.target.value) || e.target.value } })}
                          />
                        </div>
                        <div className="report-designer-field">
                          <label>Parameter mapping JSON</label>
                          <input
                            value={JSON.stringify(linkTo.parameterMapping || {})}
                            onChange={(e) => {
                              try {
                                updateColumn(columnName, { linkTo: { ...linkTo, type: 'report', parameterMapping: JSON.parse(e.target.value || '{}') } });
                              } catch (err) {
                                // keep typing
                              }
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </section>

          <section className="report-designer-section">
            <div className="report-designer-row">
              <h2>Advanced JSON</h2>
              <button
                type="button"
                className="report-designer-btn secondary"
                onClick={() => {
                  setAdvancedOpen((open) => !open);
                  setAdvancedFilters(JSON.stringify(draft.filters, null, 2));
                  setAdvancedDisplay(JSON.stringify(draft.display, null, 2));
                }}
              >
                {advancedOpen ? 'Hide' : 'Show'}
              </button>
            </div>
            {advancedOpen && (
              <div className="report-designer-advanced">
                <div className="report-designer-field">
                  <label>filters</label>
                  <textarea value={advancedFilters} onChange={(e) => setAdvancedFilters(e.target.value)} />
                </div>
                <div className="report-designer-field">
                  <label>display</label>
                  <textarea value={advancedDisplay} onChange={(e) => setAdvancedDisplay(e.target.value)} />
                </div>
                <button type="button" className="report-designer-btn" onClick={applyAdvancedJson}>Apply JSON</button>
              </div>
            )}
          </section>

          {previewRows.length > 0 && (
            <section className="report-designer-section">
              <h2>Preview</h2>
              <div className="report-designer-preview-wrap">
                <table className="report-designer-preview-table">
                  <thead>
                    <tr>{previewColumns.map((col) => <th key={col}>{col}</th>)}</tr>
                  </thead>
                  <tbody>
                    {previewRows.slice(0, 100).map((row, index) => (
                      <tr key={index}>
                        {previewColumns.map((col) => <td key={col}>{String(row[col] ?? '')}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>

        <aside className="report-designer-ai">
          <h2>AI copilot</h2>
          <p className="report-designer-muted">
            Describe the report in plain language. The assistant drafts SQL, filters, and display config. Nothing is saved until you apply and save. Patient rows are never sent.
          </p>
          <div className="report-designer-ai-log">
            {aiLog.length === 0 && <p className="report-designer-muted">No messages yet.</p>}
            {aiLog.map((entry, index) => (
              <p key={index}>
                <strong>{entry.role === 'user' ? 'You' : 'AI'}:</strong> {entry.text}
                {entry.sqlValid === false ? ' (SQL needs review)' : ''}
              </p>
            ))}
          </div>
          <div className="report-designer-field">
            <label htmlFor="ai-instruction">Instruction</label>
            <textarea
              id="ai-instruction"
              rows={5}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="e.g. Admissions by ward last 30 days, link patient_number to the patient card, red if still admitted"
            />
          </div>
          <div className="report-designer-header-actions" style={{ marginTop: 8 }}>
            <button type="button" className="report-designer-btn" onClick={askAi} disabled={aiBusy}>
              {aiBusy ? 'Asking…' : 'Ask AI'}
            </button>
            <button type="button" className="report-designer-btn success" onClick={applyAiDraft} disabled={!pendingAi || pendingAi.sqlValid === false}>
              Apply draft
            </button>
          </div>
          {pendingAi?.warnings && (
            <p className="report-designer-muted" style={{ marginTop: 8 }}>
              {Array.isArray(pendingAi.warnings) ? pendingAi.warnings.join(' ') : String(pendingAi.warnings)}
            </p>
          )}
        </aside>
      </div>
    </div>
  );
};

function defaultsFromFilters(filters) {
  const values = {};
  (filters || []).forEach((filter) => {
    values[filter.name] = filter.default || '';
  });
  return values;
}

export default ReportDesigner;
